import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { DirectionalHint } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { getId } from "@fluentui/react";
import { useMergedRefs } from "@fluentui/react-hooks";
import type { RefObjectFunction } from "@fluentui/react-hooks";

import type { UILocation } from "@1js/acui-common";

import type { AppTooltipHost } from "../AppTooltipHost";
import { useAppTooltipHost } from "../AppTooltipHost";
import { getTooltipText } from "./getTooltipText";

// eslint-disable-next-line no-restricted-imports
import { useV9TooltipEnabled } from "../hooks/useV9TooltipEnabled";

/**
 * Returns the given ref merged with a local ref controlling a new AppTooltipHost instance
 * initialized with the given tooltip parameters. Once the returned ref is attached to your
 * component, the component will have a tooltip. TooltipHostRef is also provided when
 * it is needed to call TooltipHost's imperative APIs.
 */
export const useAppComponentTooltips = (
  ref: React.Ref<any>,
  opts?: {
    customTooltip?: string;
    hideLabel?: boolean;
    id?: string;
    label?: string;
    direction?: DirectionalHint;
    location?: UILocation;
    neverShowTooltip?: boolean;
  }
): {
  mergedRef: RefObjectFunction<HTMLElement | null | undefined>;
  tooltipHostRef: React.MutableRefObject<AppTooltipHost | undefined>;
} => {
  const {
    customTooltip,
    hideLabel,
    id,
    label = "",
    direction,
    location,
    neverShowTooltip,
  } = opts || {};
  const tooltipId = React.useMemo(() => id || getId("tooltip-"), [id]);
  const tooltipText = getTooltipText(hideLabel, label, customTooltip);
  const { tooltipRef, tooltipHostRef } = useAppTooltipHost(tooltipId, {
    label: tooltipText,
    direction,
    location,
  });

  const v9TooltipEnabled = useV9TooltipEnabled();
  // Since hooks cannot be called conditionally, if we don't want to
  // ever show a tooltip for a component, let's just exclude the ref
  // created and pass in null instead
  const mergedRef = useMergedRefs<HTMLElement | null | undefined>(
    ref,
    neverShowTooltip || v9TooltipEnabled ? null : tooltipRef
  );
  return { mergedRef, tooltipHostRef };
};
